import type { BookingDetailUI } from "../booking-detail.types";

function formatDateKo(dateStr: string) {
  const date = new Date(dateStr);
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일 (${dayNames[date.getDay()]})`;
}

function calcDday(dateStr: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "D-Day";
  if (diff > 0) return `D-${diff}`;
  return `D+${Math.abs(diff)}`;
}

function timeToMinutes(time: string) {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function calcTotalDuration(booking: BookingDetailUI): string {
  const { slots } = booking;
  if (!slots.length) return "-";
  const first = timeToMinutes(slots[0].time);
  const last = timeToMinutes(slots[slots.length - 1].endTime);
  const diff = last - first;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  if (m === 0) return `${h}시간`;
  return `${h}시간 ${m}분`;
}

interface Props {
  booking: BookingDetailUI;
}

export default function WeddingScheduleCard({ booking }: Props) {
  const tourDday = calcDday(booking.date);
  const weddingDday = booking.weddingDate ? calcDday(booking.weddingDate) : null;
  const shootingDday = booking.shootingDate ? calcDday(booking.shootingDate) : null;
  const totalDuration = calcTotalDuration(booking);

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ border: "1px solid #DBEAFE", background: "linear-gradient(145deg, #EFF6FF 0%, #DBEAFE 60%, #EFF6FF 100%)" }}
    >
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#2563EB" }}>
                <i className="ri-calendar-check-line text-xs" style={{ color: "white" }}></i>
              </div>
              <span className="text-xs font-semibold tracking-wide uppercase" style={{ color: "#2563EB" }}>드레스 투어 일정</span>
            </div>
            <p className="text-xl font-bold leading-tight" style={{ color: "#1E3A5F" }}>
              {formatDateKo(booking.date)}
            </p>
          </div>
          <div
            className="flex-shrink-0 px-3.5 py-2 rounded-2xl text-center"
            style={{ backgroundColor: "#2563EB" }}
          >
            <p className="text-lg font-bold leading-none" style={{ color: "white" }}>{tourDday}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ backgroundColor: "rgba(37,99,235,0.12)" }}
          >
            <i className="ri-store-2-line text-xs" style={{ color: "#2563EB" }}></i>
            <span className="text-xs font-semibold" style={{ color: "#1D4ED8" }}>
              {booking.slots.length}개 업체 방문
            </span>
          </div>
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ backgroundColor: "rgba(37,99,235,0.12)" }}
          >
            <i className="ri-time-line text-xs" style={{ color: "#2563EB" }}></i>
            <span className="text-xs font-semibold" style={{ color: "#1D4ED8" }}>
              {booking.slots[0]?.time} 시작 · 총 {totalDuration}
            </span>
          </div>
        </div>
      </div>

      {(booking.weddingDate || booking.shootingDate) && (
        <div
          className="px-5 py-3 flex items-center gap-0"
          style={{ borderTop: "1px solid rgba(37,99,235,0.15)", backgroundColor: "rgba(255,255,255,0.6)" }}
        >
          {booking.shootingDate && (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <i className="ri-camera-line text-xs" style={{ color: "#6B7280" }}></i>
                <span className="text-xs" style={{ color: "#6B7280" }}>촬영 예정일</span>
              </div>
              {
                (booking.noShooting == 1) ? (
                  <p className="text-sm font-bold">미진행</p>
                ) : (booking.shootingDateUndecided == 1) ? (
                  <p className="text-sm font-bold">미정</p>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold" style={{ color: "#374151" }}>
                      {new Date(booking.shootingDate).toLocaleDateString("ko-KR", { year: 'numeric', month: "long", day: "numeric" })}
                    </p>
                    {shootingDday && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ backgroundColor: "#F3F4F6", color: "#6B7280" }}
                      >
                        {shootingDday}
                      </span>
                    )}
                  </div>
                )
              }
            </div>
          )}
          {booking.shootingDate && booking.weddingDate && (
            <div className="w-px h-10 flex-shrink-0 mx-4" style={{ backgroundColor: "rgba(37,99,235,0.2)" }}></div>
          )}
          {booking.weddingDate && (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <i className="ri-heart-line text-xs" style={{ color: "#F59E0B" }}></i>
                <span className="text-xs" style={{ color: "#6B7280" }}>본식 예정일</span>
              </div>
              {
                booking.weddingDateUndecided == 1 ? <p className='text-sm'>미정</p> : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold" style={{ color: "#374151" }}>
                      {new Date(booking.weddingDate).toLocaleDateString("ko-KR", { year: 'numeric', month: "long", day: "numeric" })}
                    </p>
                    {weddingDday && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}
                      >
                        {weddingDday}
                      </span>
                    )}
                  </div>
                )
              }
              
            </div>
          )}
        </div>
      )}
    </div>
  );
}